import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { OverrideActionField } from './OverrideActionField';
import { OverrideSectionPrimaryButton } from './OverrideSectionPrimaryButton';
import { OverrideSectionTransferSourceField } from './OverrideSectionTransferSourceField';
import { OverrideSectionVendorField } from './OverrideSectionVendorField';
import { formatOverrideCurrency } from './overrideSectionFormat';
import type { useOverrideSectionController } from './useOverrideSectionController';

const INPUT_CLASS = 'h-8 rounded border border-gray-300 px-2';

type Controller = ReturnType<typeof useOverrideSectionController>;

export function OverrideSectionFormPanel({ ctrl }: { ctrl: Controller }) {
  const gt = useGT();
  const {
    bulkLineCount,
    vendors,
    sources,
    formDraft,
    patchFormDraft,
    effectiveAction,
    actionChoices,
    effectiveVendorId,
    effectiveSourceId,
    selectedVendor,
    isBuy,
    isTransfer,
    qty,
    canSubmit,
    submit,
    isSaving,
    setOpen,
  } = ctrl;
  const { quantity } = formDraft;

  return (
    <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50/40 p-4">
      <div className="text-sm font-medium text-gray-700">
        <T context="override form heading">Custom option</T>
      </div>
      {bulkLineCount > 1 ? (
        <p className="text-xs font-medium text-sky-800">
          {gt('{n} lines selected on the board', {
            context: 'bulk fulfillment override banner',
            n: bulkLineCount,
          })}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <OverrideActionField
          choices={actionChoices}
          value={effectiveAction}
          onChange={(next) => patchFormDraft({ action: next })}
          inputClass={INPUT_CLASS}
        />
        <label className="flex flex-col gap-1">
          <span className="text-gray-500">
            <T context="override field quantity">Quantity</T>
          </span>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => patchFormDraft({ quantity: e.target.value })}
            className={INPUT_CLASS}
          />
        </label>

        {isTransfer ? (
          <OverrideSectionTransferSourceField
            sources={sources}
            effectiveSourceId={effectiveSourceId}
            onSourceIdChange={(sourceId) => patchFormDraft({ sourceId })}
            inputClass={INPUT_CLASS}
          />
        ) : null}

        {isBuy ? (
          <OverrideSectionVendorField
            vendors={vendors}
            effectiveVendorId={effectiveVendorId}
            onVendorIdChange={(vendorId) => patchFormDraft({ vendorId })}
            inputClass={INPUT_CLASS}
          />
        ) : null}
      </div>

      {isBuy && selectedVendor ? (
        <OverrideBuyCostSummary
          unitPrice={selectedVendor.unitPrice}
          qty={qty}
        />
      ) : null}

      <div className="flex gap-2">
        <OverrideSectionPrimaryButton
          isSaving={isSaving}
          bulkLineCount={bulkLineCount}
          canSubmit={canSubmit}
          onClick={() => void submit()}
        />
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          <T context="override cancel">Cancel</T>
        </Button>
      </div>
    </div>
  );
}

function OverrideBuyCostSummary({
  unitPrice,
  qty,
}: {
  unitPrice: number | null;
  qty: number;
}) {
  const gt = useGT();
  return (
    <div className="text-xs text-gray-500">
      {gt('Unit cost', { context: 'override derived cost label' })}:{' '}
      {formatOverrideCurrency(unitPrice)}
      {unitPrice != null && qty > 0 ? (
        <>
          {' · '}
          {gt('Total', {
            context: 'override derived total label',
          })}
          : {formatOverrideCurrency(unitPrice * qty)}
        </>
      ) : null}
    </div>
  );
}
